import { type ReactNode, type DetailedHTMLProps, type HTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "~/lib/utils";
import { applyBreakpoint } from "~/utils/Helpers";

const FONT_SIZE_CLASSES = {
    "2xl": "text-[24px]",
    "xl": "text-[20px]",
    "lg": "text-[18px]",
    "md": "text-[16px]",
    "sm": "text-[14px]",
    "xs": "text-[12px]",
    "2xs": "text-[10px]",
} as const;

const bodyVariants = cva("font-suisseBP", {
    variants: {
        size: FONT_SIZE_CLASSES,
        weight: {
            "300": "font-light",
            "400": "font-normal",
            "500": "font-medium",
        },
        style: {
            "normal": "",
            "italic": "italic",
        },
    },
    defaultVariants: {
        size: "md",
        weight: "400",
        style: "normal",
    },
});

type ResponsiveSize = {
    sm?: VariantProps<typeof bodyVariants>["size"];
    md?: VariantProps<typeof bodyVariants>["size"];
    lg?: VariantProps<typeof bodyVariants>["size"];
    xl?: VariantProps<typeof bodyVariants>["size"];
};

type Props = DetailedHTMLProps<HTMLAttributes<HTMLParagraphElement>, HTMLParagraphElement> & VariantProps<typeof bodyVariants> & {
    children?: ReactNode;
    as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span" | "sub" | "sup" | "i" | "em";
    className?: string;
    sizes?: ResponsiveSize;
}

export const Paragraph = ({ children, as: Tag = "p", className, size, sizes = {}, weight, style, ...rest }: Props) => {
    const responsiveClasses = Object.entries(sizes).map(([bp, variant]) => {
        return applyBreakpoint(bp, [FONT_SIZE_CLASSES[variant!]]);
    }).join(" ");

    return (
        <Tag className={cn(bodyVariants({ size, weight, style }), responsiveClasses, "leading-[1.3]", className)} {...rest}>{children}</Tag>
    );
};

export default Paragraph;
